import moment from 'moment';
import React from 'react';
import { PolylineProps, PushpinProps } from '../../types/MapModels';
import { IDirectionModel, IRouteModel } from '../../types/view-models-interfaces';
import { loadRoutes } from './_apiActions';
import { loadSpeedMapData } from './_loadData';
import { AgencyModel, buildRoute, DirectionCallback, DirectionDetSpeedsSet, RouteVariantEntry, RouteVariantKey, RouteVariantsMap, SpeedRangePolyline, SpeedRangeSet, timeApiToUi, TimePeriodKey, timeUiToApi } from './_models';
import { StopsDetailsLevel } from './_stops-details-level';
import { TreckDetailsLevel } from './_treck-details-level';

type InputDate = {
    id: number;
    upperBoundDate: string;
    lowerBoundDate: string;
    upperBoundTime: string;
    lowerBoundTime: string;
};

type PeriodOption = { text: string; value: string | number };

export type SpeedMapContextType = {
    agency: AgencyModel | null;
    setAgency: (agency: AgencyModel) => void;
    isBusy: boolean;
    setBusy: (busy: boolean) => void;
    showComparePeriods: boolean;
    setShowComparePeriods: (value: boolean) => void;
    dataPeriodsNumber: number;
    setDataPeriodsNumber: (value: number) => void;
    inputDates: InputDate[];
    setLowerBoundDate: (value: string, id: number) => void;
    setUpperBoundDate: (value: string, id: number) => void;
    setLowerBoundTime: (value: string, id: number) => void;
    setUpperBoundTime: (value: string, id: number) => void;
    excludeWeekends: boolean;
    setExcludeWekends: (value: boolean) => void;
    excludeStops: boolean;
    setExcludeStops: (value: boolean) => void;
    canLoadNewData: () => boolean;
    onMapReady: (container: HTMLDivElement) => void;
    routesList: IRouteModel[] | null;
    setRoutesList: (routes: IRouteModel[]) => void;
    selectRouteDirection: (route: IRouteModel, direction: IDirectionModel) => void;
    handleGoClick: () => void;
    directionHasData: (routeName: string, routeId: string, cardinalDirection: string) => number;
    periodsState: { options: PeriodOption[]; selectedValue: string | number };
    setSelectedPeriod: (value: string | number) => void;
    speedRangeSet: SpeedRangeSet;
    updateSpeedRanges: () => void;
    treckDetailsLevel: TreckDetailsLevel;
    setTreckDetailsLevel: (level: TreckDetailsLevel) => void;
    stops: { items: any[] };
    stopsDetailsLevel: StopsDetailsLevel;
    setStopsDetailsLevel: (level: StopsDetailsLevel) => void;
    mapPolylines: PolylineProps[];
    mapPushpins: PushpinProps[];
};

const buildDefaultDates = (): InputDate[] => {
    const today0 = moment().set({ minute: 0, second: 0, millisecond: 0 });
    const lowerBoundDate1 = today0.clone().add(-2, 'days').format('YYYY-MM-DD');
    const upperBoundDate1 = today0.clone().add(-1, 'days').format('YYYY-MM-DD');
    const lowerBoundDate2 = today0.clone().add(-3, 'days').format('YYYY-MM-DD');
    const upperBoundDate2 = today0.clone().add(-2, 'days').format('YYYY-MM-DD');
    const timeFrom = timeUiToApi('09:00 AM');
    const timeTo = timeUiToApi('12:00 PM');
    return [
        { id: 1, lowerBoundDate: lowerBoundDate1, upperBoundDate: upperBoundDate1, lowerBoundTime: timeFrom, upperBoundTime: timeTo },
        { id: 2, lowerBoundDate: lowerBoundDate2, upperBoundDate: upperBoundDate2, lowerBoundTime: timeFrom, upperBoundTime: timeTo },
    ];
};

function buildPeriodOptions(inputDates: InputDate[]): PeriodOption[] {
    return inputDates.map(selectedDates => ({
        value: String(selectedDates.id),
        text: `${selectedDates.lowerBoundDate} ${timeApiToUi(selectedDates.lowerBoundTime)} to ${selectedDates.upperBoundDate} ${timeApiToUi(selectedDates.upperBoundTime)}`,
    }));
}

function toMapPolylines(polylines: SpeedRangePolyline[] | undefined): PolylineProps[] {
    return (polylines || []).map(polyline => ({
        locations: polyline.locations,
        options: polyline.options,
    }));
}

export const SpeedMapContext = React.createContext<SpeedMapContextType>(null as unknown as SpeedMapContextType);

export const SpeedMapState: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [agency, setAgencyState] = React.useState<AgencyModel | null>(null);
    const [isBusy, setBusy] = React.useState(false);
    const [showComparePeriods, setShowComparePeriodsState] = React.useState(false);
    const [dataPeriodsNumber, setDataPeriodsNumber] = React.useState(0);
    const [inputDates, setInputDates] = React.useState<InputDate[]>(buildDefaultDates);
    const [excludeWeekends, setExcludeWekendsState] = React.useState(false);
    const [excludeStops, setExcludeStopsState] = React.useState(false);
    const [routesList, setRoutesListState] = React.useState<IRouteModel[] | null>(null);
    const [selectedPeriod, setSelectedPeriodState] = React.useState<string | number>('1');
    const [speedRangeSet, setSpeedRangeSet] = React.useState(SpeedRangeSet.load);
    const [treckDetailsLevel, setTreckDetailsLevel] = React.useState<TreckDetailsLevel>(TreckDetailsLevel.L0);
    const [stopsDetailsLevel, setStopsDetailsLevel] = React.useState<StopsDetailsLevel>(StopsDetailsLevel.L0);
    const [stops, setStops] = React.useState<{ items: any[] }>({ items: [] });
    const [mapPolylines, setMapPolylines] = React.useState<PolylineProps[]>([]);
    const [mapPushpins, setMapPushpins] = React.useState<PushpinProps[]>([]);
    const [selectedRoute, setSelectedRoute] = React.useState<IRouteModel | null>(null);
    const [selectedDirection, setSelectedDirection] = React.useState<IDirectionModel | null>(null);
    const primaryLowerBoundDate = inputDates.find(d => d.id === 1)?.lowerBoundDate;

    const routeVariantsMapRef = React.useRef(new RouteVariantsMap());
    const shouldReloadDataRef = React.useRef(false);
    const selectedRouteRef = React.useRef<IRouteModel | null>(null);
    const selectedDirectionRef = React.useRef<IDirectionModel | null>(null);

    React.useEffect(() => {
        selectedRouteRef.current = selectedRoute;
    }, [selectedRoute]);

    React.useEffect(() => {
        selectedDirectionRef.current = selectedDirection;
    }, [selectedDirection]);

    const clearMapData = React.useCallback(() => {
        setMapPolylines([]);
        setMapPushpins([]);
        setStops({ items: [] });
    }, []);

    const markDataDirty = React.useCallback(() => {
        shouldReloadDataRef.current = true;
    }, []);

    const updateDate = React.useCallback((id: number, updater: (existing: InputDate) => InputDate) => {
        setInputDates(prev => prev.map(d => d.id === id ? updater(d) : d));
        markDataDirty();
    }, [markDataDirty]);

    const setLowerBoundDate = React.useCallback((value: string, id: number) => {
        updateDate(id, d => ({ ...d, lowerBoundDate: value }));
    }, [updateDate]);

    const setUpperBoundDate = React.useCallback((value: string, id: number) => {
        updateDate(id, d => ({ ...d, upperBoundDate: value }));
    }, [updateDate]);

    const setLowerBoundTime = React.useCallback((value: string, id: number) => {
        updateDate(id, d => ({ ...d, lowerBoundTime: value }));
    }, [updateDate]);

    const setUpperBoundTime = React.useCallback((value: string, id: number) => {
        updateDate(id, d => ({ ...d, upperBoundTime: value }));
    }, [updateDate]);

    const processRawSpeedData = React.useCallback((
        rawSpeedData: DirectionDetSpeedsSet[],
        ranges: SpeedRangeSet,
        excludeStopsFlag: boolean,
    ) => {
        const callback: DirectionCallback = (
            key: RouteVariantKey,
            polylines: SpeedRangePolyline[],
            bounds,
            timePeriodKey: TimePeriodKey,
            detailsLevel: TreckDetailsLevel,
        ) => {
            const routeVariantEntry = routeVariantsMapRef.current.get(key);
            if (!routeVariantEntry) {
                return;
            }

            routeVariantEntry.setData(timePeriodKey, detailsLevel, bounds, polylines, rawSpeedData);
        };

        rawSpeedData.forEach(speedData => buildRoute(speedData, ranges, excludeStopsFlag, callback));
    }, []);

    const applySelectedRouteToMap = React.useCallback((route: IRouteModel, direction: IDirectionModel, setId: number) => {
        const key = new RouteVariantKey(route.routeName, route.routeId, direction.cardinalDirection);
        const entry = routeVariantsMapRef.current.get(key);
        const polylines = entry?.getPolylines(new TimePeriodKey(setId), treckDetailsLevel);

        setMapPolylines(toMapPolylines(polylines));
        setMapPushpins([]);
        setStops({ items: [] });
    }, [treckDetailsLevel]);

    const loadRoutesList = React.useCallback(async (currentAgency: AgencyModel) => {
        if (!primaryLowerBoundDate) {
            return;
        }

        routeVariantsMapRef.current = new RouteVariantsMap();
        clearMapData();
        setRoutesListState(null);

        try {
            const routes = await loadRoutes(currentAgency.id, primaryLowerBoundDate);
            const prevRoute = selectedRouteRef.current;
            const prevDirection = selectedDirectionRef.current;
            let matchedRoute: IRouteModel | null = null;
            let matchedDirection: IDirectionModel | null = null;

            routes.forEach(route => {
                route.directions.forEach(direction => {
                    const isSelected =
                        route.routeId === prevRoute?.routeId &&
                        route.routeName === prevRoute?.routeName &&
                        direction.directionVariantId === prevDirection?.directionVariantId;

                    direction.selected = isSelected;
                    if (isSelected) {
                        matchedRoute = route;
                        matchedDirection = direction;
                    }

                    const key = new RouteVariantKey(route.routeName, route.routeId, direction.cardinalDirection);
                    routeVariantsMapRef.current.set(key, new RouteVariantEntry(route, direction));
                });
            });

            setRoutesListState(routes);

            if (matchedRoute && matchedDirection) {
                setSelectedRoute(matchedRoute);
                setSelectedDirection(matchedDirection);
            } else {
                setSelectedRoute(null);
                setSelectedDirection(null);
                clearMapData();
            }
        } catch (err) {
            console.error('SpeedMapContext: loadRoutes failed', err);
            setRoutesListState([]);
            setSelectedRoute(null);
            setSelectedDirection(null);
            clearMapData();
        }
    }, [clearMapData, primaryLowerBoundDate]);

    React.useEffect(() => {
        if (!agency) {
            return;
        }

        void loadRoutesList(agency);
    }, [agency, loadRoutesList, primaryLowerBoundDate]);

    const onMapReady = React.useCallback((_container: HTMLDivElement) => {
        // TransitMap owns Azure initialization. SpeedMap only provides declarative data.
    }, []);

    const selectRouteDirection = React.useCallback((route: IRouteModel, direction: IDirectionModel) => {
        const key = new RouteVariantKey(route.routeName, route.routeId, direction.cardinalDirection);
        const entry = routeVariantsMapRef.current.get(key) || new RouteVariantEntry(route, direction);
        routeVariantsMapRef.current.set(key, entry);

        setSelectedRoute(route);
        setSelectedDirection(direction);
        clearMapData();
    }, [clearMapData]);

    const canLoadNewData = React.useCallback(() => {
        const primary = inputDates.find(d => d.id === 1);
        if (!primary) {
            return false;
        }

        return Boolean(
            agency &&
            selectedRoute &&
            selectedDirection &&
            primary.lowerBoundDate &&
            primary.upperBoundDate &&
            primary.lowerBoundTime &&
            primary.upperBoundTime,
        );
    }, [agency, inputDates, selectedDirection, selectedRoute]);

    const loadSelectedRouteData = React.useCallback(async (
        route: IRouteModel,
        direction: IDirectionModel,
        setId: number,
        overrides?: {
            speedRanges?: SpeedRangeSet;
            excludeStops?: boolean;
            comparePeriods?: boolean;
        },
    ) => {
        if (!agency) {
            return;
        }

        const comparePeriods = overrides?.comparePeriods ?? showComparePeriods;
        const speedRanges = overrides?.speedRanges ?? speedRangeSet;
        const excludeStopsFlag = overrides?.excludeStops ?? excludeStops;
        const key = new RouteVariantKey(route.routeName, route.routeId, direction.cardinalDirection);
        let routeVariantEntry = routeVariantsMapRef.current.get(key);

        if (!routeVariantEntry) {
            routeVariantEntry = new RouteVariantEntry(route, direction);
            routeVariantsMapRef.current.set(key, routeVariantEntry);
        }

        const timePeriodKey = new TimePeriodKey(setId);
        const needsLoad =
            shouldReloadDataRef.current ||
            !routeVariantEntry.hasPolylineSets(timePeriodKey) ||
            !routeVariantEntry.hasSpeedDataSets(timePeriodKey);

        if (needsLoad) {
            setBusy(true);
            try {
                const rawSpeedData = await Promise.all(
                    inputDates
                        .filter(d => comparePeriods ? true : d.id === 1)
                        .map(d => loadSpeedMapData(
                            agency.id,
                            d.id,
                            direction.directionVariantId,
                            d.lowerBoundDate,
                            d.lowerBoundTime,
                            d.upperBoundDate,
                            d.upperBoundTime,
                            excludeWeekends,
                        )),
                );

                processRawSpeedData(rawSpeedData, speedRanges, excludeStopsFlag);
                setDataPeriodsNumber(rawSpeedData.length);
                shouldReloadDataRef.current = false;
            } finally {
                setBusy(false);
            }
        }

        setSelectedPeriodState(String(setId));
        applySelectedRouteToMap(route, direction, setId);
    }, [
        agency,
        applySelectedRouteToMap,
        excludeStops,
        excludeWeekends,
        inputDates,
        processRawSpeedData,
        showComparePeriods,
        speedRangeSet,
    ]);

    const handleGoClick = React.useCallback(() => {
        if (!selectedRoute || !selectedDirection) {
            return;
        }

        const activeSetId = showComparePeriods ? Number(selectedPeriod) : 1;
        void loadSelectedRouteData(selectedRoute, selectedDirection, activeSetId);
    }, [loadSelectedRouteData, selectedDirection, selectedPeriod, selectedRoute, showComparePeriods]);

    const directionHasData = React.useCallback((routeName: string, routeId: string, cardinalDirection: string) => {
        const polylineSets = routeVariantsMapRef.current.get(new RouteVariantKey(routeName, routeId, cardinalDirection))?.getPolylineSets();
        let result = 0;

        if (polylineSets) {
            if (showComparePeriods) {
                if (polylineSets[0] !== undefined) {
                    result += 1;
                }

                if (polylineSets[1] !== undefined) {
                    result += 1;
                }
            } else if (polylineSets[0] !== undefined) {
                result += 2;
            }
        }

        return result;
    }, [showComparePeriods]);

    const setSelectedPeriod = React.useCallback((value: string | number) => {
        setSelectedPeriodState(value);

        if (!selectedRouteRef.current || !selectedDirectionRef.current) {
            return;
        }

        void loadSelectedRouteData(
            selectedRouteRef.current,
            selectedDirectionRef.current,
            Number(value),
        );
    }, [loadSelectedRouteData]);

    const updateSpeedRanges = React.useCallback(() => {
        const nextRanges = SpeedRangeSet.load();
        setSpeedRangeSet(nextRanges);
        shouldReloadDataRef.current = true;

        if (selectedRouteRef.current && selectedDirectionRef.current) {
            const activeSetId = showComparePeriods ? Number(selectedPeriod) : 1;
            void loadSelectedRouteData(selectedRouteRef.current, selectedDirectionRef.current, activeSetId, {
                speedRanges: nextRanges,
            });
        }
    }, [loadSelectedRouteData, selectedPeriod, showComparePeriods]);

    const setAgency = React.useCallback((nextAgency: AgencyModel) => {
        setAgencyState(nextAgency);
    }, []);

    const setRoutesList = React.useCallback((routes: IRouteModel[]) => {
        setRoutesListState(routes);
    }, []);

    const setExcludeWekends = React.useCallback((value: boolean) => {
        setExcludeWekendsState(value);
        markDataDirty();
    }, [markDataDirty]);

    const setExcludeStops = React.useCallback((value: boolean) => {
        setExcludeStopsState(value);
        markDataDirty();
    }, [markDataDirty]);

    const setShowComparePeriods = React.useCallback((value: boolean) => {
        setShowComparePeriodsState(value);
        markDataDirty();

        if (!value) {
            setSelectedPeriodState('1');
        }
    }, [markDataDirty]);

    const value = React.useMemo<SpeedMapContextType>(() => ({
        agency,
        setAgency,
        isBusy,
        setBusy,
        showComparePeriods,
        setShowComparePeriods,
        dataPeriodsNumber,
        setDataPeriodsNumber,
        inputDates,
        setLowerBoundDate,
        setUpperBoundDate,
        setLowerBoundTime,
        setUpperBoundTime,
        excludeWeekends,
        setExcludeWekends,
        excludeStops,
        setExcludeStops,
        canLoadNewData,
        onMapReady,
        routesList,
        setRoutesList,
        selectRouteDirection,
        handleGoClick,
        directionHasData,
        periodsState: {
            options: buildPeriodOptions(inputDates),
            selectedValue: selectedPeriod,
        },
        setSelectedPeriod,
        speedRangeSet,
        updateSpeedRanges,
        treckDetailsLevel,
        setTreckDetailsLevel,
        stops,
        stopsDetailsLevel,
        setStopsDetailsLevel,
        mapPolylines,
        mapPushpins,
    }), [
        agency,
        canLoadNewData,
        dataPeriodsNumber,
        directionHasData,
        excludeStops,
        excludeWeekends,
        handleGoClick,
        inputDates,
        isBusy,
        mapPolylines,
        mapPushpins,
        onMapReady,
        routesList,
        selectRouteDirection,
        selectedPeriod,
        setAgency,
        setExcludeStops,
        setExcludeWekends,
        setLowerBoundDate,
        setRoutesList,
        setSelectedPeriod,
        setShowComparePeriods,
        setUpperBoundDate,
        setLowerBoundTime,
        setUpperBoundTime,
        showComparePeriods,
        speedRangeSet,
        stops,
        stopsDetailsLevel,
        treckDetailsLevel,
        updateSpeedRanges,
    ]);

    return (
        <SpeedMapContext.Provider value={value}>
            {children}
        </SpeedMapContext.Provider>
    );
};

export default SpeedMapContext;
